import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';
import type { LightSeason } from '@/pages/docs/lab/design/lightSeason';

/**
 * 로그인 화면 설정 데이터 접근(`settings_login` · 사용자 지시 2026-09-15).
 *
 * 백엔드 `/api/v1/login-design` 에 붙는다. **읽는 쪽은 로그인 앞에 있다** — `/screen` 은 인증
 * 없이 열린다(`LoginDesignController` 머리말). 그래서 이 훅을 로그인 화면이 그대로 쓴다.
 *
 * <h2>사진은 같은 오리진으로 받는다</h2>
 * presigned URL 이 아니다. 로그인 전이라 그것을 받아 갈 세션이 없고, 오리진이 스토리지라
 * CSP·mixed content 에 걸린다 — 평면도 뷰어(`useFloorplan`)와 같은 판단이다.
 */

export type LoginGrid = {
  /** 한 장이 머무는 시간(초). */
  holdSec: number;
  /** 다음 장으로 넘어가는 데 걸리는 시간(초). */
  switchSec: number;
  /** 가로 칸 수. 줄 수는 화면 비율에서 따라온다. */
  cols: number;
};

export type LoginParallax = {
  intensity: number;
  /** 1 에 가까울수록 밀 자리가 없다. */
  uvScale: number;
  multiplier: number;
  /** 스스로 흐르는 속도(초당 픽셀). 로그인 화면에는 굴릴 것이 없어 원본에 없던 값을 하나 더 뒀다. */
  speed: number;
};

export type LoginTrail = {
  count: number;
  speed: number;
  /** 작을수록 꼬리가 길다. */
  delta: number;
  /** 반지름 = 화면 대각선 ÷ 이 값. */
  divisor: number;
  color1: string;
  color2: string;
  /** 겹침 세기. 꼬리는 사진 **위**에 얹히므로 1 이면 사진이 통째로 가린다. */
  opacity: number;
};

export type LoginWaves = {
  speed: number;
  /** 커서 자리의 점 반지름. 첫 고리는 원본대로 이 값의 **세 배**에서 시작한다. */
  dotRadius: number;
  /** 고리 사이 간격(px). 촘촘할수록 물결이 굵다. 고리 수는 화면 대각선에서 따라온다. */
  ringGap: number;
  lineWidth: number;
  /** 고리 하나마다 늘어나는 지연(ms). 클수록 바깥 고리가 늦게 따라와 크게 출렁인다. */
  spreadMs: number;
  /** 선 불투명도. 원본은 0.15 — 그것은 어두운 단색 바탕에서의 값이다. */
  opacity: number;
  color1: string;
  color2: string;
  /** 켜면 위아래 색을 쓰지 않고 색상환을 돈다. 두 색은 남겨 둔다 — 끄면 그리로 돌아온다. */
  rainbow: boolean;
};

/**
 * 히어로 셰이더 한 벌(구체 · 알갱이 · 배경 돔).
 *
 * <p>`bg` · `orb` · `particleTint` 셋은 씬이 읽는 `HeroLook` 과 <b>같은 모양</b>이다 — 그래야
 * 이 객체를 그대로 넘길 수 있다. `tintEnabled` 가 꺼지면 `particleTint` 자리에 `null` 을 넣어
 * 넘긴다(원본 팔레트 그대로).
 */
export type LoginHero = {
  /**
   * 켜면 아래 색들의 색상(hue)이 다 같이 색상환을 따라 돈다. 시작 각도는 화면이 열릴 때
   * 무작위로 뽑는다 — 같은 화면을 두 번 열어도 같은 색으로 시작하지 않는다.
   */
  random: boolean;
  /** 색상환 한 바퀴에 걸리는 시간(초). 길수록 천천히 변한다. */
  spinSec: number;
  bg: {
    color1: string;
    color2: string;
    color3: string;
    color4: string;
    /** `color3` 만 누르는 밝기. 1 이면 그대로. */
    color3Brightness: number;
  };
  orb: {
    ambient: string;
    clouds: string;
    /** 테두리 — 바깥으로 갈수록 이 색이 된다. */
    fresnel: string;
    light: string;
    light2: string;
  };
  /** 알갱이·섬광을 `tint` 계열로 옮길 것인가. 끄면 원본 팔레트 그대로다. */
  tintEnabled: boolean;
  /** 그 색. 꺼 둔 동안에도 지우지 않는다 — 다시 켜면 그 색으로 돌아온다. */
  tint: string;
  /** 구체가 화면에 보이는 지름(px). 창 크기를 타지 않는다 — 큰 화면에서도 이 크기 그대로다. */
  orbSize: number;
};

/** 커서 효과는 <b>둘 중 하나</b>다. 둘을 같이 그리면 화면에 커서가 둘인 것처럼 보인다. */
export type CursorStyle = 'trail' | 'waves';

export type LoginImage = {
  /** 저장소 오브젝트 키. 화면은 이 키로 같은 오리진의 공개 경로를 부른다. */
  key: string;
  /** 올린 사람이 본 파일명(관리 화면 표시용). */
  name: string | null;
  /** 큰 글자. 무작위 격자만 그린다. */
  caption: string | null;
  /** 작은 글자. 비우면 격자가 번호를 대신 찍는다. */
  subtext: string | null;
};

/** 아파트 단지(실험실 「배치 메시 · 블록 바닥」 의 단지 디자인). 강조색은 CI 적색으로 고정이다. */
export type LoginApt = {
  /** 켜면 밤 — 커서를 올린 주변 동에만 불이 켜진다. 끄면 낮. */
  night: boolean;
  /**
   * 하루 한 바퀴(초) — 0 이면 돌지 않고 {@link night} 대로 고정. 돌 때는 {@link night} 가 시작 시각만 정한다
   * (사용자 지시 2026-10-08 「3분 주기」 · 「시간도 설정」). 앱 장면처럼 이 칸을 안 주면 돌지 않는다.
   */
  cycleSec?: number;
};

/**
 * 수족관 마릿수(사용자 지시 2026-10-20 「상어 / 거북이 / 고래 / 물고기 숫자 설정 가능하게」). 재시작 전 서버는 이 칸을
 * 보내지 않고, 칸마다 비어 있을 수 있다 — 읽을 때 {@link LOGIN_SEA_DEFAULT} 로 메운다.
 */
export type LoginSea = {
  whales?: number;
  fish?: number;
  sharks?: number;
  turtles?: number;
  mantas?: number;
  /**
   * 켜면 위 수를 쓰지 않고 화면이 설 때마다 {@link LOGIN_SEA_RANDOM} 범위에서 무작위로 뽑는다(사용자 지시 2026-10-20
   * 「새로고침이나 화면이 렌더될 때마다 수량이 무작위로」). 적어 둔 수는 지우지 않는다 — 끄면 그 수로 돌아온다.
   */
  random?: boolean;
};

/**
 * 수족관 마릿수의 기본 · 한도. 실험실 {@code DeepSea} 의 {@code DEEP_SEA_*} 와 같은 값이다 — 그쪽을 import 하면 three 가
 * 로그인 첫 묶음에 딸려 와서 낱값으로 옮겨 둔다(서버 {@code LoginDesign.Sea} 도 같은 값).
 */
export const LOGIN_SEA_DEFAULT = { whales: 1, fish: 500, sharks: 1, turtles: 1, mantas: 2 } as const;
export const LOGIN_SEA_MAX = { whales: 6, fish: 5000, sharks: 4, turtles: 6, mantas: 4 } as const;

/** 「수량 무작위」 의 범위(양 끝 포함 · 사용자 지시 2026-10-20). */
export const LOGIN_SEA_RANDOM = {
  whales: [0, 2],
  sharks: [0, 4],
  turtles: [0, 2],
  fish: [100, 500],
  mantas: [0, 2],
} as const satisfies Record<'whales' | 'sharks' | 'turtles' | 'fish' | 'mantas', readonly [number, number]>;

/** 지금 설 마릿수 — 무작위가 켜져 있으면 범위에서 뽑고, 아니면 적어 둔 수(빈 칸은 기본값). 부르는 쪽이 한 번만 부른다. */
export function resolveLoginSea(sea: LoginSea | undefined) {
  const roll = ([lo, hi]: readonly [number, number]) => lo + Math.floor(Math.random() * (hi - lo + 1));
  if (sea?.random === true) {
    return {
      whales: roll(LOGIN_SEA_RANDOM.whales),
      sharks: roll(LOGIN_SEA_RANDOM.sharks),
      turtles: roll(LOGIN_SEA_RANDOM.turtles),
      fish: roll(LOGIN_SEA_RANDOM.fish),
      mantas: roll(LOGIN_SEA_RANDOM.mantas),
    };
  }
  return {
    whales: sea?.whales ?? LOGIN_SEA_DEFAULT.whales,
    sharks: sea?.sharks ?? LOGIN_SEA_DEFAULT.sharks,
    turtles: sea?.turtles ?? LOGIN_SEA_DEFAULT.turtles,
    fish: sea?.fish ?? LOGIN_SEA_DEFAULT.fish,
    mantas: sea?.mantas ?? LOGIN_SEA_DEFAULT.mantas,
  };
}

export type LoginDesign = {
  gridEnabled: boolean;
  parallaxEnabled: boolean;
  cursorEnabled: boolean;
  cursorStyle: CursorStyle;
  heroEnabled: boolean;
  /** 아파트 단지 장면(사용자 지시 2026-10-08). */
  aptEnabled: boolean;
  /** 수영장 장면 — 실험실 「물 파문」 그대로(사용자 지시 2026-10-08). */
  poolEnabled: boolean;
  /** 세계 지도 장면 — 현장 자리마다 빛나는 건물이 솟는다(사용자 지시 2026-10-08). 재시작 전 서버는 모른다. */
  worldEnabled: boolean;
  /**
   * 단지 야경 — 창 · 지붕 · 마당의 불빛만으로 연출이 도는 장면(사용자 지시 2026-10-19). 다른 셋과 달리 디자인아트 후보에
   * 드는 것도 이 스위치를 켠 뒤다 — 새로 낸 것은 닫아서 낸다. 재시작 전 서버는 모른다(꺼짐).
   */
  lightsEnabled: boolean;
  /**
   * 야경 시즌 연출 — 고르면 평소 막 대신 그 시즌의 막(할로윈: 박쥐와 달 · 정전 · 글자 · 불꽃 / 크리스마스: 눈 · 트리 · 글자 ·
   * 불꽃)이 돈다(사용자 지시 2026-10-21). null 은 평소대로. 재시작 전 서버는 이 칸을 보내지 않는다.
   */
  lightsSeason?: LightSeason | null;
  /**
   * 수족관 — 실험실 「물속」 의 물고기 떼 · 가오리 · 상어 · 거북이 · 혹등고래(사용자 지시 2026-10-20). 야경처럼 켠 뒤에야
   * 디자인아트 후보에 든다. 재시작 전 서버는 이 칸을 보내지 않는다 — 읽을 때는 {@code === true} 로 본다.
   */
  seaEnabled?: boolean;
  sea?: LoginSea;
  /**
   * 열대 수족관 — 실험실 「열대 수족관」 의 아마존 담수어 13종 · 모래 바닥 · 유목 · 수초(사용자 지시 2026-10-28 「기존 수족관과
   * 별도로」). 마릿수는 종마다 범위에서 장면이 뽑는다 — 설정 칸이 없다. 수족관처럼 켠 뒤에야 디자인아트 후보에 든다. 재시작 전
   * 서버는 이 칸을 보내지 않는다 — 읽을 때는 {@code === true} 로 본다.
   */
  tankEnabled?: boolean;
  /**
   * 거리 걷기 — 대한민국 거리 · 골목을 걷는 실제 영상이 차례로 겹쳐 넘어간다(사용자 지시 2026-10-29 「3D 말고 골목을 다니는
   * 거로」). 사진 시안처럼 화면 전체에 깔리고 카드는 가운데 선다. 재시작 전 서버는 이 칸을 보내지 않는다 — {@code === true} 로 본다.
   */
  walkEnabled?: boolean;
  /**
   * 로그인 창에 「디자인아트」 스위치를 둘 것인가(사용자 지시 2026-10-08). 켜고 저장해야 나타난다 — 꺼져
   * 있으면 스위치도 장면도 없이 카드만 선다. 재시작 전 서버는 모른다(꺼짐으로 읽힌다).
   */
  designArtEnabled: boolean;
  apt: LoginApt;
  grid: LoginGrid;
  parallax: LoginParallax;
  trail: LoginTrail;
  waves: LoginWaves;
  hero: LoginHero;
  images: LoginImage[];
};

/**
 * 바탕에 서는 시안 하나. 다섯은 모두 같은 자리(바탕)를 다투므로 하나만 선다.
 * `null` 은 「바탕 시안 없음」 — 커서 효과만 서거나, 아무것도 서지 않는다.
 *
 * <p><b>사진 둘(격자 · 시차)</b>은 사진을 까는 시안이고, <b>장면 셋(히어로 · 단지 · 수영장)</b>은
 * 사진 없이 왼쪽 절반에 무언가를 세우는 시안이다.
 */
export type BackdropPick = 'grid' | 'parallax' | 'walk' | 'hero' | 'apt' | 'lights' | 'sea' | 'tank' | 'pool' | 'world' | null;

/**
 * 장면들. 서면 카드가 오른쪽 절반으로 물러나고, 커서 효과는 겹치지 않는다. 야경(`lights` · 2026-10-19)과 수족관(`sea` ·
 * 2026-10-20)은 관리자 스위치를 켜야 디자인아트 후보로 선다.
 */
const SCENES = new Set<BackdropPick>(['hero', 'apt', 'lights', 'sea', 'tank', 'pool', 'world']);

/**
 * 지금 설 수 있는 시안들. 사진 시안은 사진이 한 장도 없으면 켜 두어도 빠진다 — 검은 판이 될 뿐이다.
 *
 * <p>예전에는 히어로가 <b>지는 쪽</b>이었다(사진·커서가 하나라도 서면 빠짐, 2026-09-22). 장면이 셋이 된
 * 뒤로는 사진과 장면을 <b>한 통</b>에 넣고 켜 둔 것 중 하나를 뽑는다(사용자 결정 2026-10-08).
 */
export function backdropChoices(design: LoginDesign): Exclude<BackdropPick, null>[] {
  const photos = design.images.length > 0;
  const out: Exclude<BackdropPick, null>[] = [];
  if (photos && design.gridEnabled) out.push('grid');
  if (photos && design.parallaxEnabled) out.push('parallax');
  if (design.heroEnabled) out.push('hero');
  pushAptLights(design, out);
  if (design.poolEnabled) out.push('pool');
  if (design.worldEnabled) out.push('world');
  if (design.seaEnabled === true) out.push('sea');
  if (design.tankEnabled === true) out.push('tank');
  // 영상은 사진처럼 화면 전체에 깔린다 — 장면(SCENES)이 아니라 카드가 가운데 선다.
  if (design.walkEnabled === true) out.push('walk');
  return out;
}

/**
 * 단지와 야경은 따로 뽑지 않는다(사용자 지시 2026-10-21) — 둘 다 켜면 <b>하나의 흐름</b>(단지의 하루 → 저녁에 조명 둘 → 다시
 * 단지)이고, 단지만 켜면 조명 없는 단지, 야경만 켜면 아침 없이 조명만 끝없이 돈다. 흐름은 {@code 'lights'} 한 칸으로 들고,
 * 어느 판을 세울지는 {@code LoginBackdrop} 이 {@code aptEnabled} 로 가른다.
 */
function pushAptLights(design: LoginDesign, out: Exclude<BackdropPick, null>[]) {
  if (design.lightsEnabled) out.push('lights');
  else if (design.aptEnabled) out.push('apt');
}

/**
 * 들어올 때마다 한 번 뽑는다. 로그인 화면과 설정 미리보기가 이 결과 <b>하나</b>를 바탕과 카드 자리에
 * 같이 넘긴다 — 둘이 따로 물으면 뽑기라 답이 갈려, 단지가 깔렸는데 카드는 가운데에 서는 일이 생긴다.
 *
 * <p>후보 목록이 바뀔 때만 다시 뽑는다. 렌더마다 뽑으면 아이디를 한 글자 칠 때마다 바탕이 바뀐다.
 */
export function useBackdropPick(design: LoginDesign): BackdropPick {
  const key = backdropChoices(design).join(',');
  return useMemo<BackdropPick>(() => {
    const choices = key ? (key.split(',') as Exclude<BackdropPick, null>[]) : [];
    return choices[Math.floor(Math.random() * choices.length)] ?? null;
  }, [key]);
}

/** 장면(히어로 · 단지 · 수영장)이 섰는가 — 섰으면 카드가 오른쪽 절반으로 물러난다. */
export function sceneOnLeft(pick: BackdropPick): boolean {
  return SCENES.has(pick);
}

/** 커서 효과가 겹치는가. 장면 셋은 모두 커서를 따라가는 씬이라, 겹치면 커서 하나에 효과가 둘이 된다. */
export function cursorOverlays(design: LoginDesign, pick: BackdropPick): boolean {
  return design.cursorEnabled && !SCENES.has(pick);
}

/** 무엇이든 서는가 — 로그인 화면이 바탕을 어둡게 하고 카드에 그림자를 줄지가 이 값으로 갈린다. */
export function backdropDark(design: LoginDesign, pick: BackdropPick): boolean {
  return pick !== null || design.cursorEnabled;
}

/**
 * 「디자인아트」 — 로그인 카드 머리의 스위치(사용자 지시 2026-10-08).
 *
 * <p>환경설정 › 로그인 화면의 「디자인아트」({@code designArtEnabled})를 켜고 저장해야 스위치가 생긴다.
 * 켠 사람만 로그인 화면에 장면이 선다 — <b>단지 · 야경 · 수족관 · 수영장 · 세계 지도</b> 중 하나가 들어올 때마다
 * 무작위로(야경은 사용자 지시 2026-10-19, 수족관은 2026-10-20). 끈 사람(기본)은 바탕 없이 카드만 본다.
 *
 * <p>후보는 <b>관리자 스위치를 그대로 따른다</b>(사용자 지적 2026-10-20 「세계지도 껐는데 왜 나오냐」). 처음 판은 단지 · 수영장 ·
 * 세계 지도를 늘 후보로 두고 야경 · 수족관만 스위치로 걸렀는데, 그러면 끈 장면이 새로고침마다 나온다.
 *
 * <p>로그인 전이라 사람을 모른다 — <b>이 브라우저</b>에 남긴다(localStorage). 저장이 막힌 브라우저에서도
 * 스위치는 그 자리에서 돈다. 남지 않을 뿐이다.
 */
const DESIGN_ART_KEY = 'bomi.login.designArt';

function readDesignArt(): boolean {
  try {
    return window.localStorage.getItem(DESIGN_ART_KEY) === '1';
  } catch {
    return false;
  }
}

/** 켜짐/꺼짐과 바꾸는 손잡이. 바꾸면 이 브라우저에 남긴다. */
export function useDesignArt(): [boolean, (next: boolean) => void] {
  const [on, setOn] = useState(readDesignArt);
  const set = useCallback((next: boolean) => {
    setOn(next);
    try {
      window.localStorage.setItem(DESIGN_ART_KEY, next ? '1' : '0');
    } catch {
      // 남기지 못해도 지금 화면에서는 돈다.
    }
  }, []);
  return [on, set];
}

/**
 * 켰을 때 설 장면 하나. 켤 때마다(그리고 들어올 때마다) 한 번 뽑는다 — 렌더마다 뽑으면 아이디를 한 글자
 * 칠 때마다 바탕이 바뀐다. 꺼져 있거나 켜 둔 장면이 하나도 없으면 null(카드만).
 *
 * @param design 서버 설정 — 단지 · 수영장 · 세계 지도 · 야경 · 수족관 스위치 중 켜 둔 것만 후보다. 아직 못 받았으면 null.
 */
export function useDesignArtPick(on: boolean, design: LoginDesign | null | undefined): BackdropPick {
  const key = on && design ? designArtChoices(design).join(',') : '';
  return useMemo<BackdropPick>(() => {
    const choices = key ? (key.split(',') as Exclude<BackdropPick, null>[]) : [];
    return choices[Math.floor(Math.random() * choices.length)] ?? null;
  }, [key]);
}

function designArtChoices(design: LoginDesign): Exclude<BackdropPick, null>[] {
  const out: Exclude<BackdropPick, null>[] = [];
  pushAptLights(design, out);
  if (design.poolEnabled) out.push('pool');
  if (design.worldEnabled) out.push('world');
  if (design.seaEnabled === true) out.push('sea');
  if (design.tankEnabled === true) out.push('tank');
  if (design.walkEnabled === true) out.push('walk');
  return out;
}

/** 서버가 아직 답하지 않았거나 저장된 값이 없을 때. 셋 다 꺼짐이다(`LoginDesign.defaults()`). */
export const LOGIN_DESIGN_OFF: LoginDesign = {
  gridEnabled: false,
  parallaxEnabled: false,
  cursorEnabled: false,
  cursorStyle: 'waves',
  heroEnabled: false,
  aptEnabled: false,
  poolEnabled: false,
  worldEnabled: false,
  lightsEnabled: false,
  lightsSeason: null,
  seaEnabled: false,
  sea: { ...LOGIN_SEA_DEFAULT },
  tankEnabled: false,
  walkEnabled: false,
  designArtEnabled: false,
  apt: { night: true, cycleSec: 180 },
  grid: { holdSec: 3, switchSec: 1.6, cols: 12 },
  parallax: { intensity: 0.4, uvScale: 0.85, multiplier: 1, speed: 40 },
  trail: { count: 300, speed: 0.5, delta: 0.04, divisor: 9, color1: '#8C0FEE', color2: '#3EFFE8', opacity: 0.55 },
  waves: {
    speed: 0.5,
    dotRadius: 15,
    ringGap: 7,
    lineWidth: 1.5,
    spreadMs: 4,
    opacity: 0.3,
    color1: '#8C0FEE',
    color2: '#3EFFE8',
    rainbow: false,
  },
  // 실험실에서 맞춘 한 벌(2026-09-11) — 검은 배경 · CI 적색 구체 · 흰 테두리.
  hero: {
    random: false,
    spinSec: 180,
    bg: { color1: '#000000', color2: '#000000', color3: '#000000', color4: '#000000', color3Brightness: 0.88 },
    orb: { ambient: '#A00F16', clouds: '#A00F16', fresnel: '#F5E5E2', light: '#A00F16', light2: '#A00F16' },
    tintEnabled: true,
    tint: '#A00F16',
    orbSize: 300,
  },
  images: [],
};

const designKey = ['login-design'] as const;

/**
 * 서버 값에 없는 칸을 기본값으로 채운다. 단지·수영장 칸(2026-10-08)은 재시작 전의 서버가 모른다 —
 * 그대로 받으면 {@code design.apt.night} 에서 화면이 죽는다.
 */
function withDefaults(raw: LoginDesign): LoginDesign {
  return {
    ...LOGIN_DESIGN_OFF,
    ...raw,
    apt: { ...LOGIN_DESIGN_OFF.apt, ...(raw.apt ?? {}) },
  };
}

/** 바탕 사진 한 장을 읽는 주소. */
export function loginImageUrl(key: string): string {
  return `${runtimeConfig().apiBaseUrl}/v1/login-design/screen/image?key=${encodeURIComponent(key)}`;
}

/**
 * 지금 설정.
 *
 * <p>로그인 화면은 이것을 기다리지 않는다 — 카드는 먼저 서고 바탕만 나중에 올라온다.
 * 몇 달에 한 번 바뀌는 값이라 오래 신선한 것으로 본다.
 *
 * @param watch 창으로 돌아올 때 다시 읽을 것인가. <b>설정 화면은 끈다</b> — 거기서는 이 값이
 *   입력칸의 씨앗이라, 사진을 고르러 파일 창을 열었다 돌아온 순간 서버 값이 내려와 적던 글을
 *   덮는다. 앱 전체 기본값이 {@code refetchOnWindowFocus: true} 라(`main.tsx`) 여기서 끈다.
 */
export function useLoginDesign({ watch = true }: { watch?: boolean } = {}) {
  return useQuery({
    queryKey: designKey,
    queryFn: () => apiFetch<LoginDesign>('/v1/login-design/screen').then(withDefaults),
    staleTime: watch ? 5 * 60_000 : Infinity,
    refetchOnWindowFocus: watch,
  });
}

export function useSaveLoginDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (design: LoginDesign) => apiFetch<LoginDesign>('/v1/login-design', { method: 'PUT', body: design }),
    onSuccess: (saved) => queryClient.setQueryData(designKey, withDefaults(saved)),
  });
}

/**
 * 사진 한 장 올리기 — presigned PUT 으로 저장소에 직접 넣는다. 서버는 키만 받는다.
 *
 * <p>**저장까지 가야 등록이다.** 여기서 돌려주는 것은 키 하나이고, 그것을 목록에 얹어
 * 「저장」 을 눌러야 로그인 화면이 본다. 올려 두고 저장하지 않은 사진은 다음 저장 때
 * 서버가 치운다(`LoginDesignService.sweepUnused`).
 */
export function useUploadLoginImage() {
  return useMutation({
    mutationFn: async (file: File): Promise<LoginImage> => {
      const { key, url } = await apiFetch<{ key: string; url: string }>('/v1/login-design/uploads', {
        method: 'POST',
        body: { fileName: file.name },
      });

      const put = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!put.ok) throw new Error('사진을 저장소에 올리지 못했습니다.');

      return { key, name: file.name.replace(/\.[^.]+$/, ''), caption: null, subtext: null };
    },
  });
}
